import { Box, useTheme } from '@mui/material';
import { IconChevronLeft, IconChevronRight, IconLink } from '@tabler/icons-react';
import { shortMonthLabel, monthLabel } from '../../utils/costOfLiving';

/**
 * Month view switcher (#2139, epic #2138): previous and next arrows carrying
 * the neighbour months' short names, and a dropdown of the months with
 * entries, newest first, each marked "in progress" or "closed". With the
 * chart linked (epic decision 8) the arrows turn link blue and a link glyph
 * follows the dropdown. The row never wraps (#2179): the arrows keep their
 * width and the dropdown gives way, truncating its text on a narrow phone.
 *
 * @param {object} props
 * @param {Array<{yearMonth: string, closed: boolean}>} props.months  newest first
 * @param {string} props.value                 the shown month
 * @param {(yearMonth: string) => void} props.onChange
 * @param {boolean} [props.linked]             the chart follows this month
 * @param {Object} props.strings               engine strings
 * @param {string} props.locale
 */
const MonthSwitcher = ({ months, value, onChange, linked = false, strings, locale }) => {
  const { palette, ds } = useTheme();
  const index = months.findIndex((m) => m.yearMonth === value);
  // Newest first: the previous month is the next entry down the list.
  const previous = index >= 0 ? months[index + 1] : undefined;
  const next = index > 0 ? months[index - 1] : undefined;
  const cue = linked ? palette.cat.checking : palette.interactive;

  const arrowSx = (enabled) => ({
    display: 'inline-flex',
    alignItems: 'center',
    gap: '2px',
    border: 0,
    background: 'none',
    padding: '2px 4px',
    font: 'inherit',
    fontSize: `${ds.font.dim}px`,
    color: enabled ? cue : palette.text.dim,
    cursor: enabled ? 'pointer' : 'default',
    whiteSpace: 'nowrap',
    flexShrink: 0,
    '&:focus-visible': { outline: `1.5px solid ${palette.interactive}`, outlineOffset: '2px' },
  });

  return (
    <Box
      data-testid="month-switcher"
      data-linked={linked ? 'true' : 'false'}
      sx={{ display: 'flex', alignItems: 'center', gap: `${ds.space[2]}px`, flexWrap: 'nowrap', minWidth: 0, maxWidth: '100%' }}
    >
      <Box
        component="button"
        type="button"
        aria-label={strings['dashboardN.prevMonth']}
        disabled={!previous}
        onClick={() => previous && onChange(previous.yearMonth)}
        sx={arrowSx(Boolean(previous))}
      >
        <IconChevronLeft size={14} aria-hidden />
        {previous ? shortMonthLabel(previous.yearMonth, locale) : ''}
      </Box>
      <Box
        component="select"
        aria-label={strings['dashboardN.monthPicker']}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        sx={{
          font: 'inherit',
          fontSize: `${ds.font.meta}px`,
          color: palette.text.primary,
          backgroundColor: palette.surface.recessed,
          border: `1px solid ${palette.divider}`,
          borderRadius: '5px',
          padding: '3px 6px',
          minWidth: 0,
          flex: '0 1 auto',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          '&:focus-visible': { outline: `1.5px solid ${palette.interactive}`, outlineOffset: '2px' },
        }}
      >
        {months.map((m) => (
          <option key={m.yearMonth} value={m.yearMonth}>
            {`${monthLabel(m.yearMonth, locale)} (${strings[m.closed ? 'dashboardN.closed' : 'dashboardN.inProgress']})`}
          </option>
        ))}
      </Box>
      {linked && <IconLink size={14} aria-hidden data-testid="month-switcher-link" style={{ color: cue }} />}
      <Box
        component="button"
        type="button"
        aria-label={strings['dashboardN.nextMonth']}
        disabled={!next}
        onClick={() => next && onChange(next.yearMonth)}
        sx={arrowSx(Boolean(next))}
      >
        {next ? shortMonthLabel(next.yearMonth, locale) : ''}
        <IconChevronRight size={14} aria-hidden />
      </Box>
    </Box>
  );
};

export default MonthSwitcher;
